import _defineProperty from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/objectWithoutProperties.js";
const _excluded = ["value", "onChange"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { Input, InputGroup, InputLeftElement, InputRightElement } from '@hub/input';
import { FormLabel } from '@hub/form';
import { SearchMediumIcon } from '@hub/icon';
import ClearButton from './clear-button';
import QuitButton from './quit-button';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const DEBOUNCE_TIME = 300;

function useDebouncedHandler(handler) {
  return useDebouncedCallback(handler, DEBOUNCE_TIME, {
    leading: true
  });
}

const SearchInput = _ref => {
  let {
    autocomplete,
    placeholder,
    query,
    refsAndCursor,
    clearQuery: clearQueryFromAutocompleteState,
    onClickClose,
    reinitialiseQuery,
    setNonDebouncedQuery
  } = _ref;
  const elems = refsAndCursor.autocompleteRefs.getElems(); // If you leave value in there it wipes the field on every state change

  const _autocomplete$getInpu = autocomplete.getInputProps(elems),
        {
    value: _value,
    onChange: rawOnChange
  } = _autocomplete$getInpu,
        inputProps = _objectWithoutProperties(_autocomplete$getInpu, _excluded); // But we still need to set the input value when the query is cleared


  const clearQuery = () => {
    clearQueryFromAutocompleteState();
    setNonDebouncedQuery('');

    if (elems.inputElement) {
      elems.inputElement.value = '';
      elems.inputElement.focus();
    }
  }; // We debounce at the level of keystrokes rather than in useGetSources because doing
  // it there is an absolute pain that I can tell you about over a strong drink some time.


  const debouncedOnChange = useDebouncedHandler(e => {
    // Unfortunately autocomplete-core looks for e.currentTarget.value which stops
    // existing in a setTimeout or some nonsense, so we need to spoof it.
    const currentTarget = {
      value: e.target.value
    };
    rawOnChange === null || rawOnChange === void 0 ? void 0 : rawOnChange({
      currentTarget
    });
  });

  const onChange = e => {
    debouncedOnChange(e);
    setNonDebouncedQuery(e.target.value);
  }; // onClick also triggers search API calls so we debounce it too.


  const onClick = useDebouncedHandler(e => {
    var _inputProps$onClick;

    reinitialiseQuery();
    inputProps === null || inputProps === void 0 ? void 0 : (_inputProps$onClick = inputProps.onClick) === null || _inputProps$onClick === void 0 ? void 0 : _inputProps$onClick.call(inputProps, e);
  });
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsxs(InputGroup, {
      sx: {
        background: 'smoke'
      },
      "data-id": "search-modal-autocomplete_search-input_SearchInput_InputGroup",
      children: [/*#__PURE__*/_jsx(InputLeftElement, {
        pointerEvents: 'none',
        "data-id": "search-modal-autocomplete_search-input_SearchInput_InputGroup_InputLeftElement",
        children: /*#__PURE__*/_jsx(FormLabel, _objectSpread(_objectSpread({}, autocomplete.getLabelProps(elems)), {}, {
          htmlFor: "search-textfield-new",
          "data-id": "search-modal-autocomplete_search-input_SearchInput_InputLeftElement_FormLabel",
          children: /*#__PURE__*/_jsx(SearchMediumIcon, {
            sx: {
              cursor: 'pointer',
              position: 'absolute',
              width: 'size-icon-sm',
              height: 'size-icon-sm',
              top: 'spacing-sm',
              left: 'spacing-md',
              opacity: '1',
              stroke: 'licorice'
            },
            "data-id": "search-modal-autocomplete_search-input_SearchInput_FormLabel_SearchMediumIcon"
          })
        }))
      }), /*#__PURE__*/_jsx(Input, _objectSpread(_objectSpread({}, inputProps), {}, {
        id: "search-textfield-new",
        ref: refsAndCursor.autocompleteRefs.inputRef,
        placeholder: placeholder,
        sx: {
          margin: 'auto 45px',
          height: 'size-xs',
          padding: 'spacing-none',
          '-webkit-appearance': 'none',
          color: 'licorice',
          border: 'none',
          '&::-webkit-search-cancel-button': {
            '-webkit-appearance': 'none'
          },
          '&::-ms-clear': {
            display: 'none'
          },
          '&:focus, &:active': {
            outline: 'none'
          }
        },
        _placeholder: {
          color: 'quartz'
        },
        onChange: onChange,
        onClick: onClick,
        onFocus: reinitialiseQuery,
        onKeyDown: e => {
          if (e.key === 'ArrowDown') {
            refsAndCursor.moveDown();
            e.preventDefault();
          }
        },
        "data-id": "search-modal-autocomplete_search-input_SearchInput_InputGroup_Input"
      })), /*#__PURE__*/_jsxs(InputRightElement, {
        "data-id": "search-modal-autocomplete_search-input_SearchInput_InputGroup_InputRightElement",
        children: [/*#__PURE__*/_jsx(ClearButton, {
          clearQuery: clearQuery,
          query: query,
          "data-id": "search-modal-autocomplete_search-input_SearchInput_InputRightElement_ClearButton"
        }), /*#__PURE__*/_jsx(QuitButton, {
          sx: {
            display: ['none', 'none', 'initial']
          },
          onClickClose: onClickClose,
          "data-id": "search-modal-autocomplete_search-input_SearchInput_InputRightElement_QuitButton"
        })]
      })]
    })
  });
};

export default SearchInput;